import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  CheckCircle, Circle, Pencil, Trash2, Sparkles,
  Loader2, ChevronDown, ChevronUp, X, Check,
  MessageSquare, Send, CornerDownRight
} from "lucide-react";
import { invokeFunction } from "../../lib/invokeFunction";

export default function BriefItemRow({
  item,
  index,
  color,
  included,
  onToggle,
  onEdit,
  onDelete,
  sectionKey,
  sessionContext
}) {
  const [expanding, setExpanding] = useState(false);
  const [expansion, setExpansion] = useState(null);
  const [showExpansion, setShowExpansion] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(item);

  const [showQA, setShowQA] = useState(false);
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaHistory, setQaHistory] = useState([]);
  const [qaLoading, setQaLoading] = useState(false);

  const isPointsSection = sectionKey === "points";

  const handleExpand = async () => {
    if (expansion) {
      setShowExpansion(!showExpansion);
      return;
    }
    setExpanding(true);
    setShowExpansion(true);
    try {
      const { data, error } = await invokeFunction("expand-thesis", {
        body: {
          thesis: item,
          sectionType: sectionKey,
          sessionContext
        }
      });
      if (error) throw new Error(error.message);
      if (data?.expanded || data?.expansion) {
        setExpansion(data.expanded || data.expansion);
      }
    } catch (err) {
      console.error("Expand thesis error:", err);
      setExpansion("Не удалось получить расшифровку. Попробуйте ещё раз.");
    } finally {
      setExpanding(false);
    }
  };

  const handleAskQuestion = async () => {
    if (!qaQuestion.trim() || qaLoading) return;
    const questionText = qaQuestion.trim();
    setQaLoading(true);
    setQaQuestion("");

    const lastAiAnswer = qaHistory.length > 0
      ? qaHistory[qaHistory.length - 1].answer
      : expansion || null;

    setQaHistory((prev) => [...prev, { question: questionText, answer: null }]);

    try {
      const { data, error } = await invokeFunction("expand-thesis", {
        body: {
          thesis: item,
          sectionType: sectionKey,
          sessionContext,
          mode: "clarify",
          question: questionText,
          previousAnswer: lastAiAnswer
        }
      });
      if (error) throw new Error(error.message);
      const answer = data?.answer || "Не удалось получить ответ.";
      setQaHistory((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { question: questionText, answer };
        return updated;
      });
    } catch (err) {
      console.error("Q&A error:", err);
      setQaHistory((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          question: questionText,
          answer: "Не удалось получить ответ. Попробуйте ещё раз."
        };
        return updated;
      });
    } finally {
      setQaLoading(false);
    }
  };

  const handleSaveEdit = () => {
    if (editValue.trim()) {
      onEdit(index, editValue.trim());
    }
    setEditing(false);
  };

  const handleCancelEdit = () => {
    setEditValue(item);
    setEditing(false);
  };

  if (editing) {
    return (
      <li className="rounded-md border border-primary-500/30 bg-primary-500/5 p-3">
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          rows={3}
          autoFocus
          className="w-full px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500 resize-none mb-2"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSaveEdit();
            }
            if (e.key === "Escape") handleCancelEdit();
          }}
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancelEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-neutral-400 hover:text-white text-lg font-extralight transition-colors"
          >
            <X className="w-4 h-4" />
            Отмена
          </button>
          <button
            onClick={handleSaveEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
          >
            <Check className="w-4 h-4" />
            Сохранить
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="group border-b border-neutral-800/40 last:border-b-0 pb-3 last:pb-0">
      <div className="flex items-start gap-3">
        <button
          onClick={() => onToggle(index)}
          className="flex-shrink-0 mt-1 transition-colors"
          title={included ? "Исключить из плана" : "Включить в план"}
        >
          {included ? (
            <CheckCircle className={`w-5 h-5 ${color}`} />
          ) : (
            <Circle className="w-5 h-5 text-neutral-600 hover:text-neutral-400" />
          )}
        </button>

        <span
          className={`flex-1 text-lg font-extralight transition-colors ${
            included ? "text-neutral-200" : "text-neutral-500 line-through"
          }`}
        >
          {item}
        </span>

        <div className="hidden md:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={handleExpand}
            disabled={expanding}
            className="p-1.5 rounded-md hover:bg-primary-500/10 text-neutral-500 hover:text-primary-400 transition-colors"
            title="Расшифровать тезис"
          >
            {expanding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
          </button>
          {isPointsSection && (
            <button
              onClick={() => setShowQA(!showQA)}
              className={`p-1.5 rounded-md transition-colors ${
                showQA
                  ? "bg-blue-500/10 text-blue-400"
                  : "hover:bg-blue-500/10 text-neutral-500 hover:text-blue-400"
              }`}
              title="Задать вопрос AI по этому тезису"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setEditing(true)}
            className="p-1.5 rounded-md hover:bg-neutral-700/50 text-neutral-500 hover:text-white transition-colors"
            title="Редактировать"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(index)}
            className="p-1.5 rounded-md hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors"
            title="Удалить"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex md:hidden items-center gap-1 mt-2 ml-8">
        <button
          onClick={handleExpand}
          disabled={expanding}
          className="p-1.5 rounded-md hover:bg-primary-500/10 text-neutral-500 hover:text-primary-400 transition-colors"
          title="Расшифровать тезис"
        >
          {expanding ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
        </button>
        {isPointsSection && (
          <button
            onClick={() => setShowQA(!showQA)}
            className={`p-1.5 rounded-md transition-colors ${
              showQA
                ? "bg-blue-500/10 text-blue-400"
                : "hover:bg-blue-500/10 text-neutral-500 hover:text-blue-400"
            }`}
            title="Задать вопрос AI по этому тезису"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => setEditing(true)}
          className="p-1.5 rounded-md hover:bg-neutral-700/50 text-neutral-500 hover:text-white transition-colors"
          title="Редактировать"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(index)}
          className="p-1.5 rounded-md hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors"
          title="Удалить"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <AnimatePresence>
        {showExpansion && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="ml-0 md:ml-8 mt-2 p-3 rounded-md bg-primary-950/30 border border-primary-600/20">
              {expanding ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-primary-400 animate-spin" />
                  <span className="text-lg font-extralight text-neutral-400">
                    AI расшифровывает...
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-lg font-extralight text-primary-400">
                      Расшифровка от AI
                    </span>
                    <button
                      onClick={() => setShowExpansion(false)}
                      className="p-1 rounded hover:bg-neutral-700/50 text-neutral-500 hover:text-white transition-colors"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-lg font-extralight text-neutral-300 leading-relaxed">
                    {expansion}
                  </p>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showQA && isPointsSection && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="ml-0 md:ml-8 mt-2 p-3 rounded-md bg-blue-950/20 border border-blue-500/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg font-extralight text-blue-400 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4" />
                  Уточняющий вопрос к AI
                </span>
                <button
                  onClick={() => setShowQA(false)}
                  className="p-1 rounded hover:bg-neutral-700/50 text-neutral-500 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {qaHistory.length > 0 && (
                <div className="space-y-3 mb-3">
                  {qaHistory.map((entry, i) => (
                    <div key={i} className="space-y-2">
                      <div className="flex items-start gap-2">
                        <CornerDownRight className="w-4 h-4 text-neutral-500 flex-shrink-0 mt-1" />
                        <p className="text-lg font-extralight text-neutral-200">
                          {entry.question}
                        </p>
                      </div>
                      {entry.answer ? (
                        <div className="ml-6 p-2.5 rounded-md bg-blue-500/5 border border-blue-500/10">
                          <p className="text-lg font-extralight text-neutral-300 leading-relaxed">
                            {entry.answer}
                          </p>
                        </div>
                      ) : (
                        <div className="ml-6 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                          <span className="text-lg font-extralight text-neutral-400">
                            AI думает...
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-start gap-2">
                <textarea
                  value={qaQuestion}
                  onChange={(e) => setQaQuestion(e.target.value)}
                  rows={2}
                  placeholder="Спросите AI что-то конкретное об этом тезисе..."
                  className="flex-1 px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-blue-500 resize-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleAskQuestion();
                    }
                    if (e.key === "Escape") {
                      setShowQA(false);
                    }
                  }}
                />
                <button
                  onClick={handleAskQuestion}
                  disabled={!qaQuestion.trim() || qaLoading}
                  className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 disabled:bg-neutral-700 text-white transition-colors flex-shrink-0"
                  title="Отправить вопрос"
                >
                  {qaLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}
